export type ReceiptClientType = 'individual' | 'legal';

export type ReceiptServiceItem = {
	id: string;
	name: string;
	price: string;
};

export const RECEIPT_CLIENT_TYPE_OPTIONS: {value: ReceiptClientType; label: string}[] = [
	{value: 'individual', label: 'Физ. лицу'},
	{value: 'legal', label: 'Юр. лицу или ИП'},
];

export function createEmptyReceiptService(): ReceiptServiceItem {
	return {
		id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
		name: '',
		price: '',
	};
}

export function isReceiptServiceComplete(service: Pick<ReceiptServiceItem, 'name' | 'price'>): boolean {
	return Boolean(service.name.trim() && service.price.trim());
}

/** Цена в копейках из строки вида «10 000» / «10000». */
export function parseReceiptPriceCents(value: string): number {
	const digits = value.replace(/[^\d]/g, '');

	if (!digits) {
		return 0;
	}

	return Number(digits) * 100;
}

export function sumReceiptServicesCents(services: ReceiptServiceItem[]): number {
	return services.reduce((sum, service) => sum + parseReceiptPriceCents(service.price), 0);
}

/** Форматирует цену для карточки: «10 000 ₽». */
export function formatReceiptPrice(value: string): string {
	const digits = value.replace(/[^\d]/g, '');

	if (!digits) {
		return value.trim();
	}

	const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
	return `${formatted} ₽`;
}

export function formatReceiptTotal(cents: number): string {
	const rubles = Math.floor(cents / 100);
	const formatted = String(rubles).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
	return `${formatted} ₽`;
}

const RECEIPT_DATE_DIGITS_LENGTH = 8;

export function extractReceiptDateDigits(value: string): string {
	return value.replace(/\D/g, '').slice(0, RECEIPT_DATE_DIGITS_LENGTH);
}

/** Маска даты: «ДД.ММ.ГГГГ». */
export function formatReceiptDateDisplay(value: string): string {
	const digits = extractReceiptDateDigits(value);

	if (!digits) {
		return '';
	}

	const day = digits.slice(0, 2);
	const month = digits.slice(2, 4);
	const year = digits.slice(4);

	if (!month) {
		return day;
	}

	if (!year) {
		return `${day}.${month}`;
	}

	return `${day}.${month}.${year}`;
}

/** Парсит «ДД.ММ.ГГГГ» в ISO; иначе null. */
export function parseReceiptDateToIso(date: string): string | null {
	const match = date.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);

	if (!match) {
		return null;
	}

	const [, day, month, year] = match;
	const isoDate = `${year}-${month}-${day}`;
	const parsed = new Date(`${isoDate}T12:00:00`);

	if (Number.isNaN(parsed.getTime())) {
		return null;
	}

	return parsed.toISOString();
}
